/**
 * Issue detail — category/status pills, body, photo placeholders, notify
 * note, upvote + comment composer. Wired: GET /api/issues/:id, optimistic
 * POST /api/issues/:id/upvote, POST /api/issues/:id/comments.
 * Port of IssueDetail in design/source/screens-vendor-issue.jsx.
 */
import { useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { CardSkeletons, CatPill, goBack, PhotoPlaceholder, UpvotePill } from '@/features/community/bits';
import {
  fetchIssue, issueCatLabel, postIssueComment, timeAgo, upvoteIssue, useFetch, withUpvote, type Issue, type IssueStatus,
} from '@/features/community/lib';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, EmptyState, IconButton, Input, Pill, Screen, TopBar } from '@/ui';

const STATUS_LABEL: Record<IssueStatus, DictKey> = {
  open: 'issue_status_reported', progress: 'issue_status_progress', urgent: 'issue_status_urgent', resolved: 'issue_status_resolved',
};

/**
 * Honest, derived timeline — the API (GET /api/issues/:id) has no
 * status-change history table, only createdAt/updatedAt + the current
 * status. So this never fabricates intermediate steps like "Acknowledged by
 * committee" or "Assigned to facility manager" the design mock shows; it
 * only renders what's actually knowable:
 *   1. Reported — always, from createdAt.
 *   2. The current status — only if it has moved past "open", timestamped
 *      with updatedAt (real data) instead of a made-up date.
 * Any adminNotes the committee left are shown attached to that current step.
 */
function IssueTimeline({ issue }: { issue: Issue }) {
  const { t } = useT();
  const { colors } = useTheme();
  const reportedAt = issue.createdAt ? new Date(issue.createdAt) : null;
  const updatedAt = issue.updatedAt ? new Date(issue.updatedAt) : null;
  const hasMovedOn = issue.status !== 'open';

  type Step = { title: string; sub: string; done: boolean };
  const steps: Step[] = [
    {
      title: t('issue_status_reported'),
      sub: [issue.raisedBy, reportedAt ? timeAgo(reportedAt) : ''].filter(Boolean).join(' · '),
      done: true,
    },
  ];
  if (hasMovedOn) {
    steps.push({
      title: t(STATUS_LABEL[issue.status]),
      sub: [updatedAt ? timeAgo(updatedAt) : '', issue.adminNotes || ''].filter(Boolean).join(' · '),
      done: true,
    });
  }

  return (
    <View style={{ marginTop: space.s5 }}>
      {steps.map((s, i) => (
        <View key={i} style={{ flexDirection: 'row', gap: 14 }}>
          <View style={{ alignItems: 'center' }}>
            <View style={{
              width: 12, height: 12, borderRadius: radius.full, marginTop: 3,
              backgroundColor: s.done ? colors.primary500 : colors.canvas2,
              borderWidth: 2.5, borderColor: s.done ? colors.primary100 : colors.borderSoft,
            }} />
            {i < steps.length - 1 ? (
              <View style={{ width: 1.5, flex: 1, minHeight: 28, backgroundColor: colors.borderSoft }} />
            ) : null}
          </View>
          <View style={{ paddingBottom: 18, flex: 1 }}>
            <AppText variant="bodyMd" weight="600">{s.title}</AppText>
            {s.sub ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 3 }}>{s.sub}</AppText> : null}
          </View>
        </View>
      ))}
    </View>
  );
}

export default function IssueDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useT();
  const { colors } = useTheme();
  const { data: issue, loading, error, refetch, mutate } = useFetch(() => fetchIssue(String(id)), [id]);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const toggleUpvote = async () => {
    if (!issue) return;
    const prev = issue;
    mutate(() => withUpvote(prev));
    try {
      await upvoteIssue(prev.id);
    } catch {
      mutate(() => prev);
      setNotice(t('issue_upvote_error'));
    }
  };

  const sendComment = async () => {
    if (!issue || !comment.trim() || sending) return;
    setSending(true);
    setNotice(null);
    const prev = issue;
    const body = comment.trim();
    mutate((p) => (p ? { ...p, comments: p.comments + 1 } : p));
    setComment('');
    try {
      await postIssueComment(prev.id, body);
    } catch {
      mutate(() => prev);
      setNotice(t('issue_comment_error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('issue_detail_title')} onBack={goBack} />
      {loading ? (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <CardSkeletons count={2} />
        </Screen>
      ) : error || !issue ? (
        <Screen padded>
          <EmptyState
            icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
            title={t('issue_unavailable_title')}
            body={t('hd_detail_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : (
        <>
          <Screen padded style={{ paddingTop: space.s4, paddingBottom: 100 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
              <CatPill label={issueCatLabel(issue.category, t)} />
              <Pill status={issue.status} />
              <AppText variant="bodySm" tone="tertiary" mono style={{ marginLeft: 'auto' }}>#{issue.code}</AppText>
            </View>
            <AppText variant="headingLg" style={{ marginTop: space.s3 }}>{issue.title}</AppText>
            {issue.raisedBy || issue.when ? (
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
                {issue.raisedBy}{issue.raisedBy && issue.when ? ' · ' : ''}{issue.when}
              </AppText>
            ) : null}

            <IssueTimeline issue={issue} />

            {issue.body ? <AppText variant="bodyLg" style={{ marginTop: space.s2 }}>{issue.body}</AppText> : null}

            {issue.photos > 0 ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginTop: space.s4 }}>
                {Array.from({ length: Math.min(issue.photos, 4) }).map((_, i) => (
                  <PhotoPlaceholder key={i} h={120} style={{ width: '48%' }} />
                ))}
              </View>
            ) : null}

            {issue.notify ? (
              <View style={{
                backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s3,
                flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s4,
              }}>
                <Icon name="info" size={15} color={colors.info500} />
                <AppText variant="bodySm" tone="secondary" style={{ flex: 1 }}>{issue.notify}</AppText>
              </View>
            ) : null}

            {notice ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s4 }}>{notice}</AppText> : null}
          </Screen>

          <View style={{
            flexDirection: 'row', alignItems: 'center', gap: space.s3,
            paddingHorizontal: space.s4, paddingTop: space.s3, paddingBottom: space.s6,
            borderTopWidth: 1, borderTopColor: colors.borderSoft,
          }}>
            {issue.visibility === 'COMMUNITY' ? (
              <UpvotePill count={issue.upvotes} active={issue.upvoted} onPress={() => void toggleUpvote()} />
            ) : null}
            <Input
              value={comment}
              onChangeText={setComment}
              placeholder={t('hd_add_comment')}
              style={{ flex: 1, backgroundColor: colors.canvas1 }}
            />
            <IconButton label="Send comment" onPress={() => void sendComment()}>
              <AppText variant="headingSm" weight="600" style={{ color: comment.trim() ? colors.primary500 : colors.textTertiary }}>
                →
              </AppText>
            </IconButton>
          </View>
        </>
      )}
    </View>
  );
}
